import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const SUPPORT_EMAIL = 'support@leadmarka.co.zw';
const OPERATOR_NAME = 'YURI DIGITAL (PRIVATE) LIMITED';
const OPERATOR_DETAILS = 'Entity No. 57023A0292025, 7 Livingstone Avenue, The Legacy building, Harare, Zimbabwe';

const Privacy = () => {
  useEffect(() => {
    if (window.location.hash === '#data-deletion') {
      document.getElementById('data-deletion')?.scrollIntoView();
    }
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <Link
            to="/"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Back to app
          </Link>
          <Link
            to="/terms"
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Terms of Service
          </Link>
        </div>

        <h1 className="text-2xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-6">Effective date: September 21, 2026</p>

        <div className="space-y-4 text-sm text-gray-700">
          <p>
            LeadMarka is a product operated by {OPERATOR_NAME}, a company registered in Zimbabwe ("Yuri Digital",
            "we", "us"). This Privacy Policy explains how we collect, use, and share information when you use
            LeadMarka.
          </p>

          <h2 className="text-lg font-semibold text-gray-900">1. Information We Collect</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Account data such as your name, business name, and email address.</li>
            <li>Lead data you enter, including contact details, notes, and follow-up reminders.</li>
            <li>Settings data such as timezone and reminder preferences.</li>
            <li>
              If you connect WhatsApp Business, account and phone-number identifiers, customer profile and contact
              details, messages, media, message status, and template information that your workspace chooses to process.
            </li>
            <li>
              If your workspace enables AI features, conversation text, voice notes and transcripts, relevant CRM
              details, and approved business knowledge or catalogue content. We also create numerical representations
              called embeddings so relevant business information can be found for a request.
            </li>
            <li>Basic usage and diagnostic data to keep the service reliable and secure.</li>
          </ul>

          <p>
            The business that owns a LeadMarka workspace controls the customer data it enters or connects. That business
            is responsible for having permission to collect, upload, and use its customers' information.
          </p>

          <h2 className="text-lg font-semibold text-gray-900">2. How We Use Information</h2>
          <ul className="list-disc pl-5 space-y-1">
            <li>Provide LeadMarka features requested by your workspace.</li>
            <li>Send and receive connected WhatsApp messages, manage templates, and record delivery status.</li>
            <li>Send reminder and account-related emails.</li>
            <li>
              Provide optional AI features, including voice transcription, extracting customer-requested CRM details,
              finding relevant approved knowledge, and preparing reply drafts or automatic replies configured by the
              workspace owner.
            </li>
            <li>Respond to support requests and troubleshoot issues.</li>
            <li>Protect against fraud, abuse, or security risks.</li>
          </ul>

          <h2 className="text-lg font-semibold text-gray-900">3. How We Share Information</h2>
          <p>
            We do not sell your personal information. We share information only as needed to provide the service,
            including with Meta and WhatsApp for connected WhatsApp features; Supabase for data storage; OpenAI for
            optional AI processing of text, audio, CRM context, approved business knowledge, and embeddings; Resend for
            email delivery; Sentry for error monitoring; payment providers when you purchase a plan; or when required
            by law. These providers process information under their own applicable terms and policies. OpenAI acts as
            a service provider for these AI requests, but its handling and retention of submitted data is also governed
            by the settings and terms applicable to the service we use.
          </p>

          <h2 className="text-lg font-semibold text-gray-900">4. Data Storage and Security</h2>
          <p>
            We use industry-standard security practices to protect your information. However, no method of storage is
            completely secure, so we cannot guarantee absolute security.
          </p>

          <h2 className="text-lg font-semibold text-gray-900">5. Cookies and Local Storage</h2>
          <p>
            LeadMarka uses local storage in your browser to keep you signed in. You can clear your browser data to remove
            these tokens, but you will need to log in again.
          </p>

          <h2 id="data-deletion" className="text-lg font-semibold text-gray-900 scroll-mt-6">
            6. Data Retention and Deletion
          </h2>
          <p>
            We retain your information while your account is active and as needed to provide the service. You can delete
            leads and notes within LeadMarka. To request deletion of your account and associated personal information,
            email {SUPPORT_EMAIL} from your account email address with the subject "LeadMarka data deletion request".
            We will verify the request and delete the information we are not required to retain for legal, security, or
            fraud-prevention reasons.
          </p>
          <p>
            Clearing or deleting a LeadMarka chat removes its stored messages, internal notes, and matching retained raw
            webhook content from LeadMarka. Minimal opt-out, consent, and deleted-message identifiers may remain so we
            can honour messaging preferences and prevent deleted messages from returning through redelivery.
            Linked contacts and enquiries are separate records and are not deleted with a chat. Starting a
            new AI context only stops older messages from being used as active AI context; it does not delete chat
            history, transcripts, CRM records, or business knowledge.
          </p>
          <p>
            Routine cleanup removes successfully processed raw webhook events after seven days and other raw events
            after 30 days. AI job and transcript records are scheduled for cleanup after 30 days, and contact-import
            source files and staging data after seven days. Saved CRM records, chat history, active AI context and
            published business knowledge remain until removed through their respective deletion process.
          </p>
          <p>
            If you have connected WhatsApp Business, disconnect the integration in LeadMarka and remove LeadMarka from
            your Meta business integrations to stop future access. You may also use the deletion request above to ask us
            to remove previously stored WhatsApp-related information. Deletion in LeadMarka does not itself delete copies
            held by Meta, OpenAI, or another provider under that provider's applicable policies. You may also email the
            same address to request a portable copy of your account and CRM data; we will verify the request and provide
            the data in a practical electronic format where available.
          </p>

          <h2 className="text-lg font-semibold text-gray-900">7. International Processing</h2>
          <p>
            Meta, Supabase, OpenAI, Resend, Sentry, payment providers, and their supporting infrastructure may process
            data in countries outside your own. Where required, we use the safeguards applicable to those transfers.
          </p>

          <h2 className="text-lg font-semibold text-gray-900">8. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. We will post the updated version in the app with a new
            effective date.
          </p>

          <h2 className="text-lg font-semibold text-gray-900">9. Contact</h2>
          <p>
            The service operator and privacy contact is {OPERATOR_NAME}, {OPERATOR_DETAILS}. For privacy questions or
            requests, email us at{' '}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="text-primary-600 hover:text-primary-700 font-medium"
            >
              {SUPPORT_EMAIL}
            </a>{' '}
            or visit{' '}
            <a
              href="https://yuridigital.co.zw/"
              className="text-primary-600 hover:text-primary-700 font-medium"
              rel="noopener noreferrer"
            >
              yuridigital.co.zw
            </a>.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Privacy;
